import { Instagram, ArrowDown, Youtube, ArrowUpRight } from "lucide-react";
import { SOCIALS, PHOTOS, BRANDS } from "../data/mock";
import { motion } from "framer-motion";

/*
 * One-section Hero: bio on the left, photo + CTAs stacked on the right.
 * Copy kept tight, one line per idea, in Hunar's voice.
 */

const BioCopy = () => (
  <div
    className="space-y-5 text-lg leading-relaxed max-w-2xl"
    style={{ color: "var(--brand-muted)" }}
    data-testid="hero-subtext"
  >
    <p>
      I'm an entrepreneur building brands and businesses in public. Right now
      I'm building travel brands under the{" "}
      <a
        href="https://www.hitours.group"
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold underline underline-offset-4 hover:opacity-80"
        style={{ color: "var(--brand-ink)" }}
      >
        Hi Tours Group
      </a>
      .
    </p>
    <p>
      Since I was a kid I've been curious about entrepreneurship, business and
      tech, and how technology keeps changing the way people work. That
      curiosity is what pulls me into conversations.
    </p>
    <p>
      Outside of work, I like the gym, golf, and the sauna. Travel is a big
      part of what I do.
    </p>
    <p>
      I'm a curious person and I like hanging out with people, almost an
      extrovert. Which is why I started{" "}
      <a
        href="#show"
        className="font-semibold underline underline-offset-4 hover:opacity-80"
        style={{ color: "var(--brand-ink)" }}
      >
        Hangout with Hunar
      </a>
      . It's a different take on what a podcast is.
    </p>
  </div>
);

const BrandChips = ({ mobile = false }) => (
  <div className={mobile ? "mt-8" : "mt-9"} data-testid="hero-brands">
    <p
      className="text-[12px] font-semibold uppercase tracking-[0.25em] mb-3"
      style={{ color: mobile ? "var(--brand-accent)" : "var(--brand-accent)" }}
    >
      My brands · Hi Tours Group
    </p>
    <div className="flex flex-wrap gap-2">
      {BRANDS.map((b) => (
        <a
          key={b.name}
          href={b.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`brand-link-${b.name.toLowerCase().replace(/\s/g, "-")}`}
          title={b.desc}
          className="group inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-300"
          style={{
            border: "1px solid var(--brand-line)",
            color: "var(--brand-ink-soft)",
          }}
        >
          {b.name}
          <ArrowUpRight
            size={14}
            strokeWidth={2}
            className="opacity-50 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
          />
        </a>
      ))}
    </div>
  </div>
);

const PrimaryCtas = () => (
  <div className="flex flex-wrap gap-3" data-testid="hero-primary-ctas">
    <a
      href={SOCIALS.instagramDM}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="hero-contact-me-button"
      className="inline-flex items-center gap-2.5 rounded-full btn-primary font-semibold px-7 py-3.5"
    >
      <Instagram size={18} strokeWidth={2} />
      Contact me
    </a>
    <a
      href="#show"
      data-testid="hero-explore-show-link"
      className="inline-flex items-center gap-2 rounded-full btn-ghost font-semibold px-7 py-3.5"
    >
      Explore the show
      <ArrowDown size={16} strokeWidth={2} />
    </a>
  </div>
);

const FollowRow = () => (
  <div className="flex flex-wrap gap-2" data-testid="hero-follow-row">
    <a
      href={SOCIALS.instagram}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="hero-instagram-follow"
      className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full transition-transform duration-300 hover:-translate-y-0.5"
      style={{
        border: "1px solid var(--brand-line)",
        color: "var(--brand-ink-soft)",
      }}
    >
      <Instagram size={15} strokeWidth={1.9} style={{ color: "#DD2A7B" }} />
      Follow @hunarsyal
    </a>
    <a
      href={SOCIALS.youtube}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="hero-youtube-subscribe"
      className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full transition-transform duration-300 hover:-translate-y-0.5"
      style={{
        border: "1px solid var(--brand-line)",
        color: "var(--brand-ink-soft)",
      }}
    >
      <Youtube size={16} strokeWidth={1.9} style={{ color: "#FF0000" }} />
      Subscribe on YouTube
    </a>
  </div>
);

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative lg:pt-32 lg:pb-24 lg:px-20"
      style={{ background: "var(--brand-cream)" }}
    >
      {/* Mobile: photo header, bio + CTAs below */}
      <div className="lg:hidden">
        <div className="relative h-[62vh] min-h-[480px]">
          <img
            src={PHOTOS.bhutan}
            alt="Hunar Syal at Tiger's Nest, Bhutan"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/90 via-[#0a0a0a]/35 to-transparent" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-x-0 bottom-0 px-6 pb-7"
          >
            <h1 className="text-5xl font-bold tracking-tight leading-[1.02] text-white">
              Hey, I'm{" "}
              <span style={{ color: "var(--brand-orange)" }}>Hunar</span>
            </h1>
          </motion.div>
        </div>

        <div className="px-6 py-9">
          <BioCopy />
          <div className="mt-8">
            <PrimaryCtas />
          </div>
          <div className="mt-3">
            <FollowRow />
          </div>
          <BrandChips mobile />
        </div>
      </div>

      {/* Desktop: split */}
      <div className="hidden lg:grid max-w-7xl mx-auto lg:grid-cols-12 gap-16 items-start pt-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          <h1
            className="text-[68px] font-bold tracking-tight leading-[1.02]"
            style={{ color: "var(--brand-ink)" }}
            data-testid="hero-heading"
          >
            Hey, I'm{" "}
            <span style={{ color: "var(--brand-orange)" }}>Hunar</span>
          </h1>
          <div className="mt-7">
            <BioCopy />
          </div>
          <div className="mt-9">
            <PrimaryCtas />
          </div>
          <BrandChips />
          <div className="mt-5">
            <FollowRow />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <img
              src={PHOTOS.bhutan}
              alt="Hunar Syal at Tiger's Nest, Bhutan"
              data-testid="hero-photo"
              className="w-full aspect-[4/5] object-cover rounded-[28px]"
            />
            <div
              className="absolute bottom-5 right-5 backdrop-blur-md rounded-full px-4 py-2 text-[13px] font-medium"
              style={{
                background: "color-mix(in srgb, var(--brand-cream) 92%, transparent)",
                color: "var(--brand-ink-soft)",
              }}
            >
              Tiger's Nest, Bhutan
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
